import {AlertCircleIcon, CheckIcon, Loader2Icon, SearchIcon, SparklesIcon, Trash2Icon, UploadIcon, XIcon} from "lucide-react";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";

import {Badge} from "@components/ui/badge";
import {Button} from "@components/ui/button";
import {Card, CardContent, CardHeader, CardTitle} from "@components/ui/card";
import {Checkbox} from "@components/ui/checkbox";
import {Input} from "@components/ui/input";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@components/ui/select";
import {Skeleton} from "@components/ui/skeleton";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "@components/ui/table";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@components/ui/tooltip";
import {
    approveAllProposed,
    autoMapContacts,
    bulkApproveContacts,
    bulkAssignSegment,
    clearContactMappings,
    type ContactMappingEntry,
    deleteContactMapping,
    saveContactMapping,
    saveTabConfig,
    uploadContactMappings,
    useVNextContactMappings,
    useVNextWorkspace,
} from "@hooks/use-vnext";

interface ContactMappingTabProps {
    workspaceId: string;
    scenarioId: string;
}

// ── Inline Segment Editor ──────────────────────────────────────

function SegmentCell({
    contact,
    segments,
    workspaceId,
    onSaved,
}: {
    contact: ContactMappingEntry;
    segments: string[];
    workspaceId: string;
    onSaved: () => void;
}) {
    const [value, setValue] = useState(contact.mapping?.segment ?? "");
    const [saving, setSaving] = useState(false);
    const isProposed = contact.mapping?.status === "proposed";

    useEffect(() => {
        setValue(contact.mapping?.segment ?? "");
    }, [contact.mapping?.segment]);

    const handleBlur = useCallback(async () => {
        const current = contact.mapping?.segment ?? "";
        if (value === current) return;
        setSaving(true);
        try {
            await saveContactMapping(workspaceId, contact.contact_id, {
                segment: value,
                parent_contact_name: contact.mapping?.parent_contact_name,
            });
            onSaved();
        } catch {
            setValue(current);
        } finally {
            setSaving(false);
        }
    }, [value, contact, workspaceId, onSaved]);

    const listId = `segments-${contact.contact_id}`;

    return (
        <div className="relative">
            <input
                type="text"
                list={listId}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onBlur={handleBlur}
                onKeyDown={(e) => {
                    if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                }}
                placeholder="Enter segment..."
                disabled={saving}
                className={`w-full h-7 px-2 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-[#209DB3] disabled:opacity-50 ${
                    isProposed
                        ? "border-amber-300 bg-amber-50 dark:bg-amber-950/20"
                        : "border-border bg-background"
                }`}
            />
            <datalist id={listId}>
                {segments.map((s) => (
                    <option key={s} value={s} />
                ))}
            </datalist>
        </div>
    );
}

// ── Parent Cell (free-text input with autocomplete) ───────────

function ParentCell({
    contact,
    parentNames,
    workspaceId,
    onSaved,
}: {
    contact: ContactMappingEntry;
    parentNames: string[];
    workspaceId: string;
    onSaved: () => void;
}) {
    const [value, setValue] = useState(contact.mapping?.parent_contact_name ?? "");
    const [saving, setSaving] = useState(false);
    const isProposed = contact.mapping?.status === "proposed";

    useEffect(() => {
        setValue(contact.mapping?.parent_contact_name ?? "");
    }, [contact.mapping?.parent_contact_name]);

    const handleBlur = useCallback(async () => {
        const current = contact.mapping?.parent_contact_name ?? "";
        if (value === current) return;
        setSaving(true);
        try {
            await saveContactMapping(workspaceId, contact.contact_id, {
                segment: contact.mapping?.segment,
                parent_contact_name: value,
            });
            onSaved();
        } catch {
            setValue(current);
        } finally {
            setSaving(false);
        }
    }, [value, contact, workspaceId, onSaved]);

    const listId = `parents-${contact.contact_id}`;

    return (
        <div className="relative">
            <input
                type="text"
                list={listId}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onBlur={handleBlur}
                onKeyDown={(e) => {
                    if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                }}
                placeholder="Enter parent group..."
                disabled={saving}
                className={`w-full h-7 px-2 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-[#209DB3] disabled:opacity-50 ${
                    isProposed
                        ? "border-amber-300 bg-amber-50 dark:bg-amber-950/20"
                        : "border-border bg-background"
                }`}
            />
            <datalist id={listId}>
                {parentNames.map((n) => (
                    <option key={n} value={n} />
                ))}
            </datalist>
        </div>
    );
}

// ── Main Tab Component ─────────────────────────────────────────

export function ContactMappingTab({workspaceId}: ContactMappingTabProps) {
    const [typeFilter, setTypeFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [searchInput, setSearchInput] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [bulkSegment, setBulkSegment] = useState("");
    const [bulkSaving, setBulkSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState<string | null>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [autoMapping, setAutoMapping] = useState(false);
    const [autoMapResult, setAutoMapResult] = useState<string | null>(null);
    const [autoMapError, setAutoMapError] = useState<string | null>(null);
    const [approvingIds, setApprovingIds] = useState<Set<string>>(new Set());
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [guidelines, setGuidelines] = useState("");
    const [showGuidelinesPrompt, setShowGuidelinesPrompt] = useState(false);
    const [autoMapType, setAutoMapType] = useState<"all" | "customer" | "supplier">("all");
    const [unmappedOnly, setUnmappedOnly] = useState(true);
    const [showClearConfirm, setShowClearConfirm] = useState<"all" | "customer" | "supplier" | null>(null);
    const [clearing, setClearing] = useState(false);
    const [clearResult, setClearResult] = useState<string | null>(null);
    const [approvingAll, setApprovingAll] = useState(false);
    const [approveAllResult, setApproveAllResult] = useState<string | null>(null);
    const guidelinesLoaded = useRef(false);

    // Load guidelines from tab config
    const {tabs} = useVNextWorkspace();
    const contactTab = useMemo(() => tabs.find((t) => t.key === "contact_mapping"), [tabs]);

    useEffect(() => {
        if (contactTab && !guidelinesLoaded.current) {
            const saved = (contactTab.config as Record<string, unknown>)?.mapping_guidelines;
            if (typeof saved === "string" && saved) {
                setGuidelines(saved);
            }
            guidelinesLoaded.current = true;
        }
    }, [contactTab]);

    const persistGuidelines = useCallback((text: string) => {
        if (!contactTab) return;
        saveTabConfig(workspaceId, contactTab.id, {...(contactTab.config as Record<string, unknown>), mapping_guidelines: text});
    }, [workspaceId, contactTab]);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchInput), 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const {contacts, stats, segments, parentNames, isLoading, error, refresh} =
        useVNextContactMappings(workspaceId, typeFilter, statusFilter, debouncedSearch);

    const handleRefresh = useCallback(() => {
        refresh();
    }, [refresh]);

    // Clear selection when filters change
    useEffect(() => {
        setSelected(new Set());
    }, [typeFilter, statusFilter, debouncedSearch]);

    const toggleSelect = useCallback((contactId: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(contactId)) next.delete(contactId);
            else next.add(contactId);
            return next;
        });
    }, []);

    const toggleSelectAll = useCallback(() => {
        if (selected.size === contacts.length) {
            setSelected(new Set());
        } else {
            setSelected(new Set(contacts.map((c: ContactMappingEntry) => c.contact_id)));
        }
    }, [contacts, selected.size]);

    const handleBulkAssign = useCallback(async () => {
        if (!bulkSegment.trim() || selected.size === 0) return;
        setBulkSaving(true);
        try {
            await bulkAssignSegment(workspaceId, Array.from(selected), bulkSegment.trim());
            setSelected(new Set());
            setBulkSegment("");
            refresh();
        } finally {
            setBulkSaving(false);
        }
    }, [workspaceId, selected, bulkSegment, refresh]);

    const handleDeleteMapping = useCallback(
        async (contactId: string) => {
            try {
                await deleteContactMapping(workspaceId, contactId);
                refresh();
            } catch {
                // ignore
            }
        },
        [workspaceId, refresh],
    );

    const handleFileUpload = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (!file) return;
            setUploading(true);
            setUploadResult(null);
            setUploadError(null);
            try {
                const result = await uploadContactMappings(workspaceId, file);
                setUploadResult(
                    `Imported ${result.created} new, updated ${result.updated}, skipped ${result.skipped} of ${result.total_rows} rows` +
                    (result.warnings.length ? `. ${result.warnings.length} warning(s).` : "."),
                );
                refresh();
            } catch (err) {
                setUploadError(err instanceof Error ? err.message : "Upload failed");
            } finally {
                setUploading(false);
                if (fileInputRef.current) fileInputRef.current.value = "";
            }
        },
        [workspaceId, refresh],
    );

    const handleAutoMapClick = useCallback(() => {
        setShowGuidelinesPrompt(true);
    }, []);

    const handleClear = useCallback(async (contactType: "all" | "customer" | "supplier") => {
        setShowClearConfirm(null);
        setClearing(true);
        setClearResult(null);
        try {
            const result = await clearContactMappings(workspaceId, contactType);
            const label = contactType === "all" ? "" : ` ${contactType}`;
            setClearResult(`Cleared ${result.deleted}${label} mapping${result.deleted !== 1 ? "s" : ""}.`);
            refresh();
        } catch (err) {
            setClearResult(err instanceof Error ? err.message : "Clear failed");
        } finally {
            setClearing(false);
        }
    }, [workspaceId, refresh]);

    const runAutoMap = useCallback(async (guidelinesText: string) => {
        setShowGuidelinesPrompt(false);
        persistGuidelines(guidelinesText);
        setAutoMapping(true);
        setAutoMapResult(null);
        setAutoMapError(null);
        try {
            const result = await autoMapContacts(workspaceId, guidelinesText, autoMapType, unmappedOnly);
            const typeLabel = autoMapType === "all" ? "" : ` (${autoMapType}s)`;
            setAutoMapResult(
                `AI proposed ${result.proposed} mapping${result.proposed !== 1 ? "s" : ""}${typeLabel}, skipped ${result.skipped} already mapped.` +
                (result.errors?.length ? ` ${result.errors.length} error(s).` : ""),
            );
            refresh();
        } catch (err) {
            setAutoMapError(err instanceof Error ? err.message : "Auto-map failed");
        } finally {
            setAutoMapping(false);
        }
    }, [workspaceId, persistGuidelines, autoMapType, unmappedOnly, refresh]);

    const handleApprove = useCallback(
        async (contactId: string) => {
            setApprovingIds((prev) => new Set(prev).add(contactId));
            try {
                await saveContactMapping(workspaceId, contactId, {action: "approve"});
                refresh();
            } finally {
                setApprovingIds((prev) => {
                    const next = new Set(prev);
                    next.delete(contactId);
                    return next;
                });
            }
        },
        [workspaceId, refresh],
    );

    const handleReject = useCallback(
        async (contactId: string) => {
            setApprovingIds((prev) => new Set(prev).add(contactId));
            try {
                await saveContactMapping(workspaceId, contactId, {action: "reject"});
                refresh();
            } finally {
                setApprovingIds((prev) => {
                    const next = new Set(prev);
                    next.delete(contactId);
                    return next;
                });
            }
        },
        [workspaceId, refresh],
    );

    // Check if any selected contacts are proposed (for bulk approve)
    const selectedProposed = useMemo(() => {
        return contacts.filter(
            (c: ContactMappingEntry) =>
                selected.has(c.contact_id) && c.mapping?.status === "proposed",
        );
    }, [contacts, selected]);

    const handleBulkApprove = useCallback(async () => {
        if (selectedProposed.length === 0) return;
        setBulkSaving(true);
        setApproveAllResult(null);
        try {
            const ids = selectedProposed.map((c: ContactMappingEntry) => c.contact_id);
            const result = await bulkApproveContacts(workspaceId, ids, "approve");
            setApproveAllResult(`Approved ${result.updated} mapping${result.updated !== 1 ? "s" : ""}.`);
            setSelected(new Set());
            refresh();
        } catch (err) {
            setApproveAllResult(`Error: ${err instanceof Error ? err.message : "Approve failed"}`);
        } finally {
            setBulkSaving(false);
        }
    }, [workspaceId, selectedProposed, refresh]);

    const handleApproveAll = useCallback(async () => {
        setApprovingAll(true);
        setApproveAllResult(null);
        try {
            const result = await approveAllProposed(workspaceId);
            setApproveAllResult(`Approved ${result.updated} mapping${result.updated !== 1 ? "s" : ""}.`);
            setSelected(new Set());
            refresh();
        } catch (err) {
            setApproveAllResult(`Error: ${err instanceof Error ? err.message : "Approve failed"}`);
        } finally {
            setApprovingAll(false);
        }
    }, [workspaceId, refresh]);

    if (isLoading && contacts.length === 0) {
        return (
            <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-4">
                    {[1, 2, 3, 4].map((i) => (
                        <Card key={i}>
                            <CardContent className="p-4">
                                <Skeleton className="h-8 w-full" />
                            </CardContent>
                        </Card>
                    ))}
                </div>
                <Skeleton className="h-64 w-full" />
            </div>
        );
    }

    if (error) {
        return (
            <Card>
                <CardContent className="flex items-center gap-2 p-6 text-red-600">
                    <AlertCircleIcon className="size-5" />
                    <span>{error}</span>
                </CardContent>
            </Card>
        );
    }

    const mappedPct = stats.total > 0 ? Math.round((stats.mapped / stats.total) * 100) : 0;

    return (
        <div className="flex flex-col gap-4 overflow-auto pb-6">
            {/* Stats cards */}
            <div className="grid gap-4 md:grid-cols-4">
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Total Contacts</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-2xl font-bold">{stats.total}</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Mapped</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-2xl font-bold text-green-600">
                            {stats.mapped}
                            <span className="text-sm font-normal text-muted-foreground ml-2">({mappedPct}%)</span>
                        </p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Proposed</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-center justify-between">
                            <p className="text-2xl font-bold text-amber-500">{stats.proposed}</p>
                            {stats.proposed > 0 && (
                                <Button
                                    size="sm"
                                    className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white"
                                    disabled={approvingAll}
                                    onClick={handleApproveAll}
                                >
                                    {approvingAll ? (
                                        <Loader2Icon className="mr-1 size-3 animate-spin" />
                                    ) : (
                                        <CheckIcon className="mr-1 size-3" />
                                    )}
                                    Approve All
                                </Button>
                            )}
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Unmapped</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-2xl font-bold text-muted-foreground">{stats.unmapped}</p>
                    </CardContent>
                </Card>
            </div>

            {/* Filters row */}
            <div className="flex items-center gap-3 flex-wrap">
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="w-[150px] h-8 text-xs border-[#5BF6C8] text-[#209DB3]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Contacts</SelectItem>
                        <SelectItem value="customer">Customers</SelectItem>
                        <SelectItem value="supplier">Suppliers</SelectItem>
                    </SelectContent>
                </Select>

                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[130px] h-8 text-xs border-[#5BF6C8] text-[#209DB3]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="mapped">Mapped</SelectItem>
                        <SelectItem value="proposed">Proposed</SelectItem>
                        <SelectItem value="unmapped">Unmapped</SelectItem>
                    </SelectContent>
                </Select>

                <div className="relative flex-1 max-w-xs">
                    <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
                    <Input
                        placeholder="Search contacts..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        className="pl-8 h-8 text-xs border-[#5BF6C8]"
                    />
                </div>

                <div className="ml-auto flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs text-amber-600 border-amber-400 hover:bg-amber-50 hover:text-amber-700 dark:hover:bg-amber-950/30"
                        disabled={autoMapping}
                        onClick={handleAutoMapClick}
                    >
                        {autoMapping ? (
                            <Loader2Icon className="mr-1.5 size-3.5 animate-spin" />
                        ) : (
                            <SparklesIcon className="mr-1.5 size-3.5" />
                        )}
                        {autoMapping ? "Mapping..." : "AI Auto-Map"}
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        className="hidden"
                        onChange={handleFileUpload}
                    />
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs text-[#209DB3] border-[#209DB3] hover:bg-[#209DB3] hover:text-white"
                        disabled={uploading}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {uploading ? (
                            <Loader2Icon className="mr-1.5 size-3.5 animate-spin" />
                        ) : (
                            <UploadIcon className="mr-1.5 size-3.5" />
                        )}
                        {uploading ? "Importing..." : "Import CSV/Excel"}
                    </Button>
                    <Select
                        value=""
                        onValueChange={(v) => setShowClearConfirm(v as "all" | "customer" | "supplier")}
                    >
                        <SelectTrigger className="w-[130px] h-8 text-xs text-red-500 border-red-300 hover:bg-red-50 dark:hover:bg-red-950/20">
                            {clearing ? (
                                <span className="flex items-center gap-1.5">
                                    <Loader2Icon className="size-3.5 animate-spin" />
                                    Clearing...
                                </span>
                            ) : (
                                <span className="flex items-center gap-1.5">
                                    <Trash2Icon className="size-3.5" />
                                    Clear Mappings
                                </span>
                            )}
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Clear All</SelectItem>
                            <SelectItem value="customer">Clear Customers</SelectItem>
                            <SelectItem value="supplier">Clear Suppliers</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Clear confirmation */}
            {showClearConfirm && (
                <Card className="border-red-300 bg-red-50/50 dark:bg-red-950/10">
                    <CardContent className="p-4 flex items-center gap-3">
                        <AlertCircleIcon className="size-4 text-red-500 shrink-0" />
                        <span className="text-sm">
                            Delete <strong>{showClearConfirm === "all" ? "all" : showClearConfirm}</strong> contact mappings? This cannot be undone.
                        </span>
                        <div className="ml-auto flex items-center gap-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => setShowClearConfirm(null)}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="sm"
                                className="h-7 text-xs bg-red-600 hover:bg-red-700 text-white"
                                onClick={() => handleClear(showClearConfirm)}
                            >
                                <Trash2Icon className="mr-1 size-3" />
                                Confirm
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Guidelines prompt — shown when user clicks AI Auto-Map */}
            {showGuidelinesPrompt && (
                <Card className="border-amber-300 bg-amber-50/50 dark:bg-amber-950/10">
                    <CardContent className="p-4 space-y-3">
                        <div className="flex items-center gap-2">
                            <SparklesIcon className="size-4 text-amber-500" />
                            <span className="text-sm font-medium">AI Auto-Map Settings</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-xs font-medium text-muted-foreground">Map:</span>
                            <Select value={autoMapType} onValueChange={(v) => setAutoMapType(v as "all" | "customer" | "supplier")}>
                                <SelectTrigger className="w-[160px] h-7 text-xs">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Contacts</SelectItem>
                                    <SelectItem value="customer">Customers Only</SelectItem>
                                    <SelectItem value="supplier">Suppliers Only</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <label className="flex items-center gap-2 text-xs">
                            <Checkbox
                                checked={unmappedOnly}
                                onCheckedChange={(checked) => setUnmappedOnly(checked === true)}
                            />
                            <span>Unmapped contacts only</span>
                            <span className="text-muted-foreground">
                                — skips anything already mapped or rejected. Untick to also re-run rejected contacts.
                            </span>
                        </label>
                        <p className="text-xs text-muted-foreground">
                            Add any business-specific instructions for the AI. These will guide how contacts are segmented and grouped.
                        </p>
                        <textarea
                            value={guidelines}
                            onChange={(e) => setGuidelines(e.target.value)}
                            placeholder="e.g., Segment suppliers as 'Aged Care', 'Stadiums', 'SMB'. Group related entities by email domain. Parent company for all Cater Care branches is 'Cater Care'."
                            rows={3}
                            autoFocus
                            className="w-full text-xs border rounded px-2 py-1.5 bg-background focus:outline-none focus:ring-1 focus:ring-[#209DB3] resize-y placeholder:text-muted-foreground/60"
                        />
                        <div className="flex items-center gap-2 justify-end">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => setShowGuidelinesPrompt(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs text-muted-foreground"
                                onClick={() => runAutoMap("")}
                            >
                                Skip
                            </Button>
                            <Button
                                size="sm"
                                className="h-7 text-xs bg-amber-500 hover:bg-amber-600 text-white"
                                onClick={() => runAutoMap(guidelines)}
                            >
                                <SparklesIcon className="mr-1.5 size-3" />
                                Run Auto-Map
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Feedback messages */}
            {uploadResult && <p className="text-sm text-green-600">{uploadResult}</p>}
            {uploadError && <p className="text-sm text-red-600">{uploadError}</p>}
            {autoMapResult && <p className="text-sm text-amber-600">{autoMapResult}</p>}
            {autoMapError && <p className="text-sm text-red-600">{autoMapError}</p>}
            {approveAllResult && (
                <p className={`text-sm ${approveAllResult.startsWith("Error") ? "text-red-600" : "text-green-600"}`}>
                    {approveAllResult}
                </p>
            )}
            {clearResult && <p className="text-sm text-muted-foreground">{clearResult}</p>}

            {/* Bulk actions */}
            {selected.size > 0 && (
                <div className="flex items-center gap-3 p-2 bg-muted/50 rounded-lg border">
                    <span className="text-xs font-medium">{selected.size} selected:</span>
                    <Input
                        placeholder="Segment name..."
                        value={bulkSegment}
                        onChange={(e) => setBulkSegment(e.target.value)}
                        list="bulk-segments"
                        className="h-7 w-48 text-xs"
                    />
                    <datalist id="bulk-segments">
                        {segments.map((s: string) => (
                            <option key={s} value={s} />
                        ))}
                    </datalist>
                    <Button
                        size="sm"
                        className="h-7 text-xs bg-[#209DB3] hover:bg-[#209DB3]/90"
                        disabled={!bulkSegment.trim() || bulkSaving}
                        onClick={handleBulkAssign}
                    >
                        Apply
                    </Button>
                    {selectedProposed.length > 0 && (
                        <Button
                            size="sm"
                            className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white"
                            disabled={bulkSaving}
                            onClick={handleBulkApprove}
                        >
                            {bulkSaving ? (
                                <Loader2Icon className="mr-1 size-3 animate-spin" />
                            ) : (
                                <CheckIcon className="mr-1 size-3" />
                            )}
                            Approve Selected ({selectedProposed.length})
                        </Button>
                    )}
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => setSelected(new Set())}
                    >
                        Clear
                    </Button>
                </div>
            )}

            {/* Table */}
            <Card>
                <CardContent className="p-0">
                    <TooltipProvider>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-10">
                                        <input
                                            type="checkbox"
                                            checked={contacts.length > 0 && selected.size === contacts.length}
                                            onChange={toggleSelectAll}
                                            className="rounded border-border"
                                        />
                                    </TableHead>
                                    <TableHead className="w-8"></TableHead>
                                    <TableHead className="text-xs">Contact Name</TableHead>
                                    <TableHead className="text-xs w-24">Type</TableHead>
                                    <TableHead className="text-xs w-44">Segment</TableHead>
                                    <TableHead className="text-xs w-48">Parent</TableHead>
                                    <TableHead className="text-xs w-20"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {contacts.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                                            No contacts found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    contacts.map((c: ContactMappingEntry) => {
                                        const isMapped = c.mapping !== null && (c.mapping.segment || c.mapping.parent_contact_name);
                                        const isProposed = c.mapping?.status === "proposed";
                                        const isActioning = approvingIds.has(c.contact_id);
                                        const typeBadge =
                                            c.is_customer && c.is_supplier
                                                ? "Both"
                                                : c.is_customer
                                                  ? "Customer"
                                                  : "Supplier";
                                        return (
                                            <TableRow key={c.contact_id} className="group">
                                                <TableCell>
                                                    <input
                                                        type="checkbox"
                                                        checked={selected.has(c.contact_id)}
                                                        onChange={() => toggleSelect(c.contact_id)}
                                                        className="rounded border-border"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {isProposed ? (
                                                        <Tooltip>
                                                            <TooltipTrigger>
                                                                <span className="inline-block w-2 h-2 rounded-full bg-amber-400" />
                                                            </TooltipTrigger>
                                                            <TooltipContent side="right" className="text-xs">
                                                                AI proposed — review and approve
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    ) : (
                                                        <span
                                                            className={`inline-block w-2 h-2 rounded-full ${isMapped ? "bg-green-500" : "bg-gray-300"}`}
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-xs font-medium">{c.contact_name}</TableCell>
                                                <TableCell>
                                                    <Badge
                                                        variant="outline"
                                                        className={`text-[10px] ${
                                                            typeBadge === "Customer"
                                                                ? "border-blue-300 text-blue-700"
                                                                : typeBadge === "Supplier"
                                                                  ? "border-orange-300 text-orange-700"
                                                                  : "border-purple-300 text-purple-700"
                                                        }`}
                                                    >
                                                        {typeBadge}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <SegmentCell
                                                        contact={c}
                                                        segments={segments}
                                                        workspaceId={workspaceId}
                                                        onSaved={handleRefresh}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <ParentCell
                                                        contact={c}
                                                        parentNames={parentNames}
                                                        workspaceId={workspaceId}
                                                        onSaved={handleRefresh}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1">
                                                        {isProposed && (
                                                            <>
                                                                <button
                                                                    onClick={() => handleApprove(c.contact_id)}
                                                                    disabled={isActioning}
                                                                    className="text-green-600 hover:text-green-700 disabled:opacity-50"
                                                                    title="Approve"
                                                                >
                                                                    {isActioning ? (
                                                                        <Loader2Icon className="size-3.5 animate-spin" />
                                                                    ) : (
                                                                        <CheckIcon className="size-3.5" />
                                                                    )}
                                                                </button>
                                                                <button
                                                                    onClick={() => handleReject(c.contact_id)}
                                                                    disabled={isActioning}
                                                                    className="text-red-500 hover:text-red-600 disabled:opacity-50"
                                                                    title="Reject"
                                                                >
                                                                    <XIcon className="size-3.5" />
                                                                </button>
                                                            </>
                                                        )}
                                                        {isMapped && !isProposed && (
                                                            <button
                                                                onClick={() => handleDeleteMapping(c.contact_id)}
                                                                className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-500 transition-opacity"
                                                                title="Remove mapping"
                                                            >
                                                                <XIcon className="size-3.5" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TooltipProvider>
                </CardContent>
            </Card>
        </div>
    );
}
